import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { PageContainer } from '../components/layout/PageContainer';
import { LeaderboardTable } from '../components/leaderboard/LeaderboardTable';
import { fetchCompetitionLeaderboard } from '../services/competitionApi';
import toast from 'react-hot-toast';
import { Trophy } from 'lucide-react';

const Leaderboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { competitionSlug } = useCompetition();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const rows = await fetchCompetitionLeaderboard(competitionSlug);
        if (cancelled) return;
        setLeaderboard(rows);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        toast.error(language === 'en' ? 'Failed to load leaderboard' : 'נכשל בטעינת הטבלה');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionSlug, language]);

  if (loading) {
    return (
      <PageContainer>
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-48 rounded-lg bg-zinc-200" />
          <div className="h-64 rounded-2xl bg-zinc-200" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight text-zinc-900 sm:text-3xl">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-800">
              <Trophy className="h-6 w-6" />
            </span>
            {t('leaderboard')}
          </h1>
          <p className="mt-1 text-sm text-zinc-600">
            {language === 'en' ? 'Live rankings for this competition.' : 'דירוג חי לתחרות זו.'}
          </p>
        </div>
      </div>

      <LeaderboardTable
        rows={leaderboard}
        currentUserId={user?._id}
        language={language}
        onRowClick={(userId) => userId && navigate(`/user/${userId}`)}
      />
    </PageContainer>
  );
};

export default Leaderboard;
